import { useEffect, useRef, useState } from "react"
import { Box, Stack } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import {
    getHistoricalMaintenanceTask,
    getMaintenanceTask,
    getMaintenanceTaskHistory,
} from "../../controller/maintenance-library"
import { TaskEditor } from "./task-editor"
import { TaskEditorToolbar } from "./task-editor-toolbar"
import { TaskReadOnlyView } from "./task-read-only-view"
import { TaskVersionPanel } from "./task-version-panel"
import { findHistoryItemByVersion, findHistoryItemForTime } from "../lib/task-version-utils"

export function TaskView() {
    const {
        canEdit,
        embedded,
        historicalMode,
        libraryAsOf,
        libraryTaskVersion,
        readOnly,
        selectedTaskId,
        setLibraryAsOf,
        setLibraryTaskVersion,
    } = useBoundContext()
    const [editing, setEditing] = useState(false)
    const previousTaskIdRef = useRef(selectedTaskId)
    const history = getMaintenanceTaskHistory.useResults(
        selectedTaskId && selectedTaskId !== "new" ? selectedTaskId : undefined
    )
    const historicalTask = getHistoricalMaintenanceTask.useResults(
        selectedTaskId && selectedTaskId !== "new" && historicalMode
            ? { id: selectedTaskId, asOf: libraryAsOf }
            : undefined
    )
    const currentTask = getMaintenanceTask.useResults(
        !historicalMode && selectedTaskId && selectedTaskId !== "new" ? selectedTaskId : undefined
    )

    useEffect(syncEditModeOnTaskChange, [selectedTaskId, readOnly])

    if (selectedTaskId === "new") {
        if (!readOnly && !embedded) return <TaskEditor />
        return null
    }

    if (!selectedTaskId) {
        return null
    }

    const selectedHistoryItem =
        findHistoryItemByVersion(history, libraryTaskVersion) ?? findHistoryItemForTime(history, libraryAsOf)
    const readOnlyTask = selectedHistoryItem?.snapshot ?? historicalTask ?? currentTask

    return (
        <Stack direction={{ xs: "column", lg: "row" }} spacing={3} alignItems="flex-start">
            <If value={editing && !readOnly && !embedded}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <TaskEditorToolbar onDone={stopEditing} />
                    <TaskEditor />
                </Box>

                <Else>
                    <TaskReadOnlyView
                        task={readOnlyTask}
                        historyItem={selectedHistoryItem}
                        historicalMode={historicalMode}
                        canEdit={canEdit && !readOnly && !embedded}
                        onEdit={startEditing}
                    />
                </Else>
            </If>

            <If value={selectedTaskId !== "new" && !embedded}>
                <TaskVersionPanel
                    history={history || []}
                    libraryAsOf={libraryAsOf}
                    libraryTaskVersion={libraryTaskVersion}
                    onSelectVersion={handleSelectVersion}
                    onOpenAtVersionTime={handleOpenAtVersionTime}
                />
            </If>
        </Stack>
    )

    function syncEditModeOnTaskChange() {
        const newTaskJustSaved = previousTaskIdRef.current === "new" && selectedTaskId !== "new"
        previousTaskIdRef.current = selectedTaskId
        setEditing(newTaskJustSaved)
    }

    function startEditing() {
        setEditing(true)
    }

    function stopEditing() {
        setEditing(false)
    }

    function handleSelectVersion(version) {
        setLibraryTaskVersion(version)
    }

    function handleOpenAtVersionTime(createdAt, version) {
        setLibraryTaskVersion(version)
        setLibraryAsOf(createdAt, { preserveTaskVersion: true })
    }
}
